import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  GitFork,
  Inbox,
  LogOut,
  Mail,
  MoreVertical,
  Pencil,
  Share2,
  Stamp,
  Trash2,
  UserPlus,
  Users,
  UsersRound,
  Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  deleteEscritorio,
  fetchConvites,
  fetchEscritorio,
  fetchEscritorios,
  removeMembro,
  updateEscritorio,
} from '@/api/client';
import { fetchMeuCompartilhamento, fetchPedidosRemocao } from '@/api/compartilhamento';
import { fetchFilaParaDistribuir } from '@/api/distribuicao';
import { fetchEquipes } from '@/api/equipes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ErrorState } from '@/components/ui/ErrorState';
import { ConvitesPendentes } from '@/components/escritorio/ConvitesPendentes';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

import { CompartilhamentoAba } from './CompartilhamentoAba';
import { ConvidarDialog } from './ConvidarDialog';
import { ConvitesAba } from './ConvitesAba';
import { DistribuirAba } from './DistribuirAba';
import { EquipesAba } from './EquipesAba';
import { EscritorioFormDialog } from './EscritorioFormDialog';
import { MembrosAba } from './MembrosAba';
import { PecasAba } from './PecasAba';
import { PedidosAba } from './PedidosAba';
import { podeGerir, RoleBadge } from './papeis';
import { RegrasAba } from './RegrasAba';
import { useAcompanharLotes } from '@/components/escritorio/regras/useAcompanharLotes';

type Aba = 'membros' | 'equipes' | 'distribuir' | 'regras' | 'compartilhamento' | 'pecas' | 'pedidos' | 'convites';

/**
 * A página do escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md, Bloco 4a): membros,
 * equipes e convites em abas, com a aba na URL (`?aba=`). Substitui o modal de detalhe.
 * Compartilhamento (4b) e Pedidos de retirada (4c) completam as abas.
 */
export default function EscritorioPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convidarAberto, setConvidarAberto] = useState(false);
  const [editarAberto, setEditarAberto] = useState(false);
  const [confirmacao, setConfirmacao] = useState<'desativar' | 'sair' | null>(null);

  // O papel vem da lista (que o resto do app já tem em cache); o detalhe traz os membros.
  const lista = useQuery({
    queryKey: [...queryKeys.escritorios.all],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const detalhe = useQuery({
    queryKey: queryKeys.escritorios.detail(id),
    queryFn: () => fetchEscritorio(id),
    enabled: !!id,
  });
  // O papel sai do detalhe (os membros, com o de cada um): da lista, ele pode não ter
  // chegado — e sem papel a tela oferecia "Sair" ao próprio Proprietário.
  const meuMembro = detalhe.data?.membros.find((m) => m.usuario_id === user?.id);
  const meuPapel = meuMembro?.role ?? lista.data?.find((e) => e.id === id)?.meu_role ?? '';
  // Convidar e gerir equipes: Proprietário e sócios. Distribuir e decidir os pedidos: os
  // gestores (§19) — o Proprietário e quem ele marcar.
  const gestor = podeGerir(meuPapel);
  const proprietario = meuPapel === 'PROPRIETARIO';
  const distribui = proprietario || !!meuMembro?.gestor;
  const equipes = useQuery({
    queryKey: queryKeys.escritorios.equipes(id),
    queryFn: () => fetchEquipes(id),
    enabled: !!id && !!detalhe.data,
  });
  const compartilhamento = useQuery({
    queryKey: queryKeys.escritorios.compartilhamento(id),
    queryFn: () => fetchMeuCompartilhamento(id),
    enabled: !!id && !!detalhe.data,
  });
  const pedidos = useQuery({
    queryKey: queryKeys.escritorios.pedidos(id),
    queryFn: () => fetchPedidosRemocao(id),
    enabled: !!id && !!detalhe.data,
  });
  // Regra aplicada ao acervo em segundo plano: a fila se atualiza quando ela termina, em qualquer aba.
  useAcompanharLotes(id ?? '', distribui);
  const fila = useQuery({
    queryKey: queryKeys.escritorios.fila(id),
    queryFn: () => fetchFilaParaDistribuir(id),
    enabled: !!id && distribui,
  });
  const convites = useQuery({
    queryKey: queryKeys.escritorios.convites(id),
    queryFn: () => fetchConvites(id),
    enabled: !!id && gestor,
  });

  const abaPedida = searchParams.get('aba') as Aba | null;
  const aba: Aba =
    abaPedida === 'equipes' ||
    (abaPedida === 'distribuir' && distribui) ||
    (abaPedida === 'regras' && distribui) ||
    abaPedida === 'compartilhamento' ||
    abaPedida === 'pecas' ||
    abaPedida === 'pedidos' ||
    (abaPedida === 'convites' && gestor)
      ? abaPedida
      : 'membros';
  // Chegou do aceite do convite: a aba de compartilhamento abre com as boas-vindas (A5).
  const bemVindo = searchParams.get('bemvindo') === '1';
  const mudarAba = (nova: string) => setSearchParams(nova === 'membros' ? {} : { aba: nova }, { replace: true });

  const editar = useMutation({
    mutationFn: (dados: { nome: string; descricao?: string }) => updateEscritorio(id, dados),
    onSuccess: () => {
      showSuccess('Escritório atualizado');
      setEditarAberto(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const desativar = useMutation({
    mutationFn: () => deleteEscritorio(id),
    onSuccess: () => {
      showSuccess('Escritório desativado');
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
      navigate('/app/escritorios', { replace: true });
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const sair = useMutation({
    mutationFn: () => removeMembro(id, meuMembro!.id),
    onSuccess: () => {
      showSuccess('Você saiu do escritório');
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
      // Quem saiu perde a vaga paga pelo escritório (§29).
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      // O backend encerra as delegações do escritório junto (encerrar_por_saida).
      queryClient.invalidateQueries({ queryKey: ['delegacoes'] });
      navigate('/app/escritorios', { replace: true });
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (detalhe.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={32} label="Carregando o escritório" />
      </div>
    );
  }
  if (!detalhe.data) {
    const erro = classifyError(detalhe.error);
    return (
      <ErrorState
        title={erro.kind === 'not_found' ? 'Escritório não encontrado' : 'Não foi possível abrir o escritório'}
        description={
          erro.kind === 'not_found' ? 'Ele pode ter sido desativado, ou você não é mais membro.' : erro.message
        }
        actions={[
          {
            label: 'Voltar aos escritórios',
            onClick: () => navigate('/app/escritorios?todos=1'),
          },
        ]}
      />
    );
  }

  const escritorio = detalhe.data;
  const pendentes = convites.data?.filter((c) => c.status === 'PENDENTE').length ?? 0;
  // O que espera alguém: o gestor decide os dos outros; cada um acompanha os seus.
  const pedidosAguardando =
    pedidos.data?.filter((p) => p.status === 'PENDENTE' && (distribui ? !p.meu : p.meu)).length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Building2 />}
        title={escritorio.nome}
        subtitle={escritorio.descricao || undefined}
        backTo={{ path: '/app/escritorios?todos=1', label: 'Escritórios' }}
        badge={meuPapel ? <RoleBadge role={meuPapel} /> : undefined}
        actions={
          <div className="flex items-center gap-2">
            {gestor && (
              <Button onClick={() => setConvidarAberto(true)} aria-label="Convidar" className="h-11 w-11 p-0 sm:h-9 sm:w-auto sm:px-4">
                <UserPlus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Convidar</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-11 w-11 sm:h-10 sm:w-10" aria-label="Mais ações do escritório">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {proprietario && (
                  <DropdownMenuItem onClick={() => setEditarAberto(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar nome e descrição
                  </DropdownMenuItem>
                )}
                {meuMembro && !proprietario && (
                  <DropdownMenuItem onClick={() => setConfirmacao('sair')}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair do escritório
                  </DropdownMenuItem>
                )}
                {proprietario && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setConfirmacao('desativar')}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Desativar escritório
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <ConvitesPendentes />

      {(detalhe.error || equipes.error || convites.error) && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(detalhe.error || equipes.error || convites.error).message}</AlertDescription>
        </Alert>
      )}

      <Tabs value={aba} onValueChange={mudarAba}>
        <TabsList className="h-11">
          <TabsTrigger value="membros" className="gap-1.5">
            <Users className="h-4 w-4" aria-hidden="true" />
            Membros
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
              {escritorio.membros.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="equipes" className="gap-1.5">
            <UsersRound className="h-4 w-4" aria-hidden="true" />
            Equipes
          </TabsTrigger>
          {distribui && (
            <TabsTrigger value="distribuir" className="gap-1.5">
              <GitFork className="h-4 w-4" aria-hidden="true" />
              Distribuir
              {(fila.data?.total ?? 0) > 0 && (
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs tabular-nums text-primary">
                  {fila.data?.total}
                </span>
              )}
            </TabsTrigger>
          )}
          {distribui && (
            <TabsTrigger value="regras" className="gap-1.5">
              <Workflow className="h-4 w-4" aria-hidden="true" />
              Regras
            </TabsTrigger>
          )}
          <TabsTrigger value="compartilhamento" className="gap-1.5">
            <Share2 className="h-4 w-4" aria-hidden="true" />
            Compartilhamento
          </TabsTrigger>
          <TabsTrigger value="pecas" className="gap-1.5">
            <Stamp className="h-4 w-4" aria-hidden="true" />
            Peças
          </TabsTrigger>
          <TabsTrigger value="pedidos" className="gap-1.5">
            <Inbox className="h-4 w-4" aria-hidden="true" />
            Pedidos
            {pedidosAguardando > 0 && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs tabular-nums text-primary">
                {pedidosAguardando}
              </span>
            )}
          </TabsTrigger>
          {gestor && (
            <TabsTrigger value="convites" className="gap-1.5">
              <Mail className="h-4 w-4" aria-hidden="true" />
              Convites
              {pendentes > 0 && (
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs tabular-nums text-primary">
                  {pendentes}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="membros" className="pt-4">
          <MembrosAba
            escritorioId={id}
            membros={escritorio.membros}
            equipes={equipes.data ?? []}
            meuPapel={meuPapel}
            meuId={user?.id}
          />
        </TabsContent>
        <TabsContent value="equipes" className="pt-4">
          {equipes.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size={32} label="Carregando equipes" />
            </div>
          ) : (
            <EquipesAba
              escritorioId={id}
              equipes={equipes.data ?? []}
              membros={escritorio.membros}
              podeGerir={gestor}
              falhou={!!equipes.error && !equipes.data}
            />
          )}
        </TabsContent>
        {distribui && (
          <TabsContent value="distribuir" className="pt-4">
            {fila.data ? (
              <DistribuirAba
                escritorioId={id}
                fila={fila.data}
                membros={escritorio.membros}
                equipes={equipes.data ?? []}
              />
            ) : fila.error ? (
              <Alert variant="destructive">
                <AlertDescription>{classifyError(fila.error).message}</AlertDescription>
              </Alert>
            ) : (
              <div className="flex justify-center py-8">
                <Spinner size={32} label="Carregando o que há para distribuir" />
              </div>
            )}
          </TabsContent>
        )}
        {distribui && (
          <TabsContent value="regras" className="pt-4">
            <RegrasAba escritorioId={id} membros={escritorio.membros} equipes={equipes.data ?? []} />
          </TabsContent>
        )}
        <TabsContent value="compartilhamento" className="pt-4">
          {compartilhamento.data ? (
            <CompartilhamentoAba
              escritorioId={id}
              dados={compartilhamento.data}
              membros={escritorio.membros}
              equipes={equipes.data ?? []}
              meuId={user?.id}
              bemVindo={bemVindo}
            />
          ) : compartilhamento.error ? (
            <Alert variant="destructive">
              <AlertDescription>{classifyError(compartilhamento.error).message}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex justify-center py-8">
              <Spinner size={32} label="Carregando o compartilhamento" />
            </div>
          )}
        </TabsContent>
        <TabsContent value="pecas" className="pt-4">
          <PecasAba escritorio={escritorio} podeEditar={gestor} variosEscritorios={(lista.data?.length ?? 0) > 1} />
        </TabsContent>

        <TabsContent value="pedidos" className="pt-4">
          {pedidos.data ? (
            <PedidosAba escritorioId={id} pedidos={pedidos.data} podeDecidir={distribui} />
          ) : pedidos.error ? (
            <Alert variant="destructive">
              <AlertDescription>{classifyError(pedidos.error).message}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex justify-center py-8">
              <Spinner size={32} label="Carregando os pedidos" />
            </div>
          )}
        </TabsContent>
        {gestor && (
          <TabsContent value="convites" className="pt-4">
            {convites.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size={32} label="Carregando convites" />
              </div>
            ) : (
              <ConvitesAba
                escritorioId={id}
                convites={convites.data ?? []}
                onConvidar={() => setConvidarAberto(true)}
              />
            )}
          </TabsContent>
        )}
      </Tabs>

      <ConvidarDialog
        escritorioId={id}
        escritorioNome={escritorio.nome}
        open={convidarAberto}
        onOpenChange={setConvidarAberto}
      />
      <EscritorioFormDialog
        open={editarAberto}
        onOpenChange={setEditarAberto}
        inicial={escritorio}
        salvando={editar.isPending}
        onSalvar={(dados) => editar.mutate(dados)}
      />
      <ConfirmDialog
        open={confirmacao === 'desativar'}
        title="Desativar escritório"
        message={
          <>
            Desativar <strong>{escritorio.nome}</strong>? Os membros deixam de ver o que é compartilhado nele, e os
            documentos e modelos do escritório voltam a quem os criou.
          </>
        }
        confirmLabel="Desativar"
        confirmColor="error"
        loading={desativar.isPending}
        onConfirm={() => desativar.mutate()}
        onCancel={() => setConfirmacao(null)}
      />
      <ConfirmDialog
        open={confirmacao === 'sair'}
        title="Sair do escritório"
        message={
          <>
            Você deixa de ver o que é compartilhado em <strong>{escritorio.nome}</strong>. O que você compartilhava
            fica no escritório, só para leitura; para tirar algo, peça a retirada antes de sair. Para voltar,
            precisa de um novo convite.
          </>
        }
        confirmLabel="Sair"
        confirmColor="error"
        loading={sair.isPending}
        onConfirm={() => sair.mutate()}
        onCancel={() => setConfirmacao(null)}
      />
    </div>
  );
}
